import { apiGet, apiPost, apiPut, apiUpload } from './client';

export interface Profile {
  /**
   * Set by `GET`/`PUT /profile`: the visitor-facing fields differ from what is published.
   *
   * Everything on this object except the `notify_*` flags waits for Publish — the public site
   * config reads the published copy, and this page writes a draft. Read-only from the client's
   * side; sending it back is harmless (the gateway's schema ignores it).
   */
  has_unsaved_changes?: boolean;
  full_name?: string;
  phone?: string;
  headshot_url?: string | null;
  license_number?: string;
  license_state?: string;
  brokerage_name?: string;
  mls_id?: string;
  bio?: string;
  notify_new_lead?: boolean;
  notify_weekly_digest?: boolean;
  notify_product_updates?: boolean;
}

export async function getProfile(): Promise<Profile> {
  return apiGet<Profile>('/profile');
}

export async function updateProfile(data: Profile): Promise<Profile> {
  return apiPut<Profile>('/profile', data);
}

/**
 * Upload a headshot to R2 and return its hosted https URL.
 *
 * The returned URL is what gets persisted via updateProfile(). We must NOT
 * store a base64 data URL in headshot_url — the gateway's PUT /profile body
 * exceeds Fastify's request body limit and the save is rejected (413), which
 * is why uploaded photos "uploaded but didn't save". A short hosted URL also
 * satisfies the gateway's `z.string().url()` validation.
 */
export async function uploadProfileImage(file: File): Promise<{ url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiUpload<{ id: string; url: string; key: string; media_type: string }>(
    '/upload/image?media_type=agent',
    fd,
  );
  return { url: res.url };
}

/**
 * Change login email, step 1: current password + new address. The gateway emails a code to the
 * NEW address; nothing changes until step 2.
 */
export async function requestEmailChange(input: { new_email: string; password: string }): Promise<{ sent: boolean; new_email: string }> {
  return apiPost<{ sent: boolean; new_email: string }>('/account/email/change-request', input);
}

/** Change login email, step 2: the code from the new inbox. Returns the new login email. */
export async function confirmEmailChange(code: string): Promise<{ email: string }> {
  return apiPost<{ email: string }>('/account/email/change-confirm', { code });
}
